import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslationDirective } from '../../../directives/translation.directive';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TextHelper } from '../../../../../helper/text.helper';
import { DayPickerComponent } from './day-picker/day-picker.component';
import { ActionHelper } from '../../../../../helper/action.helper';

@Component({
  selector: 'fi-input',
  templateUrl: './custom-input.component.html',
  styleUrls: ['./custom-input.component.scss'],
  standalone: false,
})
export class CustomInputComponent implements AfterViewInit, OnChanges {
  private static counter = 0;
  id = 0;
  /**
   * Property paths
   *
   * Format:
   * '*' means use the element value itself if selectedValues is Iterable
   * 'Prop1.Prop2.Prop3' means use Prop1.Prop2.Prop3 of the element if selectedValues is Iterable
   */
  @Input() namePropertyPath: string | (( item: any ) => string) = 'Name';
  @Input() valuePropertyPath: string | (( item: any ) => string) = 'Id';
  @Input() selectedValuePropertyPath?: string | (( item: any ) => string);
  @Input() imagePropertyPath?: string | (( item: any ) => string);

  @Input() nameIsHtml: boolean = false;
  @Input() sectionIsHtml: boolean = false;

  @Input() displayedSelectedText?: string;

  @Input() options?: Iterable<any>;
  @Input() maxMenuHeight: number = 390;
  @Input() selectedOptionsOrValues?: Iterable<any> | string | number | Date | null;
  @Input() type: 'TEXT' | 'PHONE' | 'SEARCH' | 'SUGGEST' | 'EMAIL' | 'NUMBER' | 'SELECT' | 'MULTI_SELECT' | 'DAY' | 'TIME' = 'TEXT';
  @Input() maxLength?: number | string;
  @Input() translateOptions: boolean = false;

  @Input() hasError: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() clearOnEnterOrSelect: boolean = false;
  @Input() roundImage: boolean = false;
  @Input() enableSearch: boolean = false;
  @Input() controlName?: string;
  @Input() form?: UntypedFormGroup;

  @Input() placeholderKey: string = '';
  @Input() labelKey?: string;
  @Input() errorKey?: string;
  @Input() errorParams?: { [key: string]: any };
  @Input() hintKey?: string;
  @Input() selectedOptionsKey?: string;
  @Input() allOptionsKey?: string;
  @Input() replaceKeys: boolean = false;
  @Input() showSelectedOnTop: boolean = false;
  @Input() sectionTextKeys?: Map<number, string>;
  @Input() multiLineOptionText: boolean = false;
  @Input() wrap: boolean = false;

  @Input() additionalContentPosition: 'START' | 'END' | 'NONE' = 'NONE';


  /**
   * Marks the control as required. The reference's form elements carry a real
   * `required` attribute (elements/form-element/input/input.twig); this app had
   * been signalling it only with a "*" inside the label text, which no assistive
   * technology can interpret.
   */
  @Input() required: boolean = false;

  @Input() reducedHeight: boolean = false;
  @Input() notifyAboutUnsavedChanges: boolean = true;

  @Output() onChange = new EventEmitter<string>();
  @Output() onSearch = new EventEmitter<string>();
  @Output() onEnterPressed = new EventEmitter<string>();
  @Output() onDateTimeChange = new EventEmitter<Date | null>();
  @Output() onValuesChange = new EventEmitter<string[]>();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('container') container: ElementRef<HTMLElement>;
  @ViewChild('dayPicker') dayPicker: DayPickerComponent;

  private readonly optionMap = new Map<string, {
    label: string,
    image?: string,
    html?: SafeHtml,
    style?: string,
    id?: number
  }>();
  private readonly selectedValues = new Set<string>();

  oldSelectedValues = new Set<string>();
  value: string = '';
  search: string = '';
  calculatedMenuHeight = '0px';

  openDropdown: boolean = false;
  displayedOptions: {
    label: string,
    value: string,
    selected: boolean,
    image?: string,
    html?: SafeHtml,
    style?: string,
    id?: number
  }[] = [];
  showDateOrTimeDropdown = false;

  selectedHour = -1;
  selectedMinute = -1;
  selectedDay = -1;
  selectedMonth = -1;
  selectedYear = -1;
  upperDropdown = false;

  private refreshTimeout = null;

  constructor( private sanitizer: DomSanitizer ) {
    this.id = CustomInputComponent.counter++;

    const now = new Date();

    this.selectedDay = now.getDate();
    this.selectedMonth = now.getMonth();
    this.selectedYear = now.getFullYear();
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:focusin', ['$event'])
  onClick( event: MouseEvent | FocusEvent ): void {
    if ( this.type !== 'SUGGEST' && this.type !== 'SELECT' && this.type !== 'MULTI_SELECT' && this.type !== 'TIME' && this.type !== 'DAY' ) {
      return;
    }

    if ( event.target instanceof HTMLElement ) {
      if ( event.type === 'focusin' && this.container?.nativeElement && event.target.contains(this.container.nativeElement) ) {
        return;
      }

      if ( this.container?.nativeElement?.contains(event.target) ) {
        if ( event.target.closest('.arrow') && this.type !== 'TIME' && this.type !== 'DAY' ) {
          if ( !this.openDropdown ) {
            this.input.nativeElement?.focus();
            return;
          }
        } else {
          return;
        }
      }
    }

    if ( this.type === 'TIME' || this.type === 'DAY' ) {
      this.showDateOrTimeDropdown = false;
      return;
    }

    this.openDropdown = false;

    if ( this.type !== 'SUGGEST' ) {
      this.setValue(this.value);
    }
  }

  ngAfterViewInit(): void {
    this.refreshValues(30);
  }

  ngOnChanges(): void {
    this.refreshValues();
  }

  onEnter(): void {
    if ( this.type === 'SEARCH' ) {
      this.onSearch.emit(this.input.nativeElement.value);

      if ( this.clearOnEnterOrSelect ) {
        this.input.nativeElement.value = '';
      }

      return;
    }

    if ( this.type === 'SELECT' ) {
      this.onEnterPressed.emit(this.input.nativeElement.value);

      if ( this.notifyAboutUnsavedChanges ) {
        ActionHelper.notifyAboutEdit();
      }

      if ( this.clearOnEnterOrSelect ) {
        this.openDropdown = false;
        this.selectedValues.clear();
        this.setValue('');
      }

      return;
    }
  }

  onInput(): void {
    if ( this.type === 'SELECT' || this.type === 'MULTI_SELECT' || this.type === 'SUGGEST' ) {
      this.openDropdown = true;
      const value = this.input.nativeElement.value;
      this.search = value.toLowerCase();
      this.onSearch.emit(this.type === 'SUGGEST' ? value : value.toLowerCase());
      this.calculateMenuHeight();
      return;
    }

    if ( this.type === 'TEXT' || this.type === 'NUMBER' || this.type === 'EMAIL' || this.type === 'PHONE' || this.type === 'SEARCH' ) {
      this.value = this.input.nativeElement.value;
      this.onChange.emit(this.value);

      if ( this.notifyAboutUnsavedChanges ) {
        ActionHelper.notifyAboutEdit();
      }

      return;
    }
  }

  onSelectValue( value: string ): void {
    if ( this.type !== 'MULTI_SELECT' ) {
      this.selectedValues.clear();
      this.selectedValues.add(value);
    } else {
      if ( this.selectedValues.has(value) ) {
        this.selectedValues.delete(value);
      } else {
        this.selectedValues.add(value);
      }
    }


    if ( this.type === 'MULTI_SELECT' ) {
      this.onValuesChange.emit([...this.selectedValues]);

      if ( this.notifyAboutUnsavedChanges ) {
        ActionHelper.notifyAboutEdit();
      }

      this.refreshValues(50);
    } else {
      this.onChange.emit(value);

      if ( this.notifyAboutUnsavedChanges ) {
        ActionHelper.notifyAboutEdit();
      }

      (document.activeElement as HTMLElement)?.blur();
      this.openDropdown = false;

      if ( !this.clearOnEnterOrSelect ) {
        this.setValue(this.optionMap.get(value)?.label);
        this.refreshValues();
      } else {
        this.selectedValues.clear();
        this.setValue('');
        this.refreshValues();
      }


    }
  }

  /** SELECT, MULTI_SELECT and SUGGEST render a custom listbox rather than a native select. */
  get isDropdown(): boolean {
    return this.type === 'SELECT' || this.type === 'MULTI_SELECT' || this.type === 'SUGGEST';
  }

  get listboxId(): string {
    return `custom_input_${ this.id }_listbox`;
  }

  /** Points the control at whichever of the error or hint messages is rendered. */
  get describedBy(): string | null {
    if ( this.hasError && (this.errorKey || this.hintKey) ) {
      return `custom_input_${ this.id }_error`;
    }

    if ( this.hintKey ) {
      return `custom_input_${ this.id }_hint`;
    }

    return null;
  }

  get inputType(): string {
    if ( this.type === 'NUMBER' ) {
      return 'number';
    }

    if ( this.type === 'SEARCH' ) {
      return 'search';
    }

    if ( this.type === 'EMAIL' ) {
      return 'email';
    }

    if ( this.type === 'PHONE' ) {
      return 'tel';
    }

    if ( this.type === 'SUGGEST' || ((this.type === 'SELECT' || this.type === 'MULTI_SELECT') && this.enableSearch) ) {
      return 'search';
    }

    return 'text';
  }

  private scrollToDateOrTimeMenu(): void {
    setTimeout(() => {
      if ( !this.showDateOrTimeDropdown || !(this.type === 'DAY' || this.type === 'TIME') ) {
        return;
      }

      const menu = this.container?.nativeElement?.querySelector('.date-menu, .time-menu');
      menu?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }, 100);
  }

  onFocus(): void {
    if ( this.readOnly ) {
      return;
    }

    if ( this.type === 'DAY' ) {
      if ( this.selectedOptionsOrValues instanceof Date ) {
        this.selectedYear = this.selectedOptionsOrValues.getFullYear();
        this.selectedMonth = this.selectedOptionsOrValues.getMonth();
        this.selectedDay = this.selectedOptionsOrValues.getDate();
      } else if (
        this.selectedOptionsOrValues === null ||
        this.selectedYear === -1 ||
        this.selectedMonth === -1 ||
        this.selectedDay === -1
      ) {
        const now = new Date();
        this.selectedYear = now.getFullYear();
        this.selectedMonth = now.getMonth();
        this.selectedDay = now.getDate();
      }

      this.dayPicker.shownYear = this.selectedYear;
      this.dayPicker.shownMonth = this.selectedMonth;
      this.dayPicker.refresh();
      this.showDateOrTimeDropdown = true;
      this.scrollToDateOrTimeMenu();
      return;
    }

    if ( this.type === 'TIME' ) {
      if ( this.selectedOptionsOrValues instanceof Date ) {
        this.selectedHour = this.selectedOptionsOrValues.getHours();
        this.selectedMinute = this.selectedOptionsOrValues.getMinutes();
      } else if ( this.selectedOptionsOrValues === null ) {
        this.selectedHour = -1;
        this.selectedMinute = -1;
      }

      this.showDateOrTimeDropdown = true;
      this.scrollToDateOrTimeMenu();
      return;
    }

    if ( this.type !== 'SELECT' && this.type !== 'MULTI_SELECT' ) {
      return;
    }

    this.calculateMenuHeight();

    if ( this.type === 'MULTI_SELECT' && this.openDropdown && this.enableSearch ) {
      return;
    }

    if ( this.enableSearch && window.innerWidth <= 880 ) {
      if ( !this.openDropdown ) {
        this.input?.nativeElement?.blur();
      } else {
        return;
      }
    }

    this.openDropdown = !this.openDropdown;

    if ( this.openDropdown ) {
      this.calculatedMenuHeight = '0px';

      if ( this.allOptionsKey && this.selectedOptionsKey || this.showSelectedOnTop ) {
        this.oldSelectedValues = new Set(this.selectedValues);
      }

      this.search = '';

      if ( this.enableSearch ) {
        this.onSearch.emit(this.search);

        if ( this.type === 'MULTI_SELECT' ) {
          if ( this.input?.nativeElement ) {
            this.input.nativeElement.value = '';
          }
        }

        return;
      }

      setTimeout(() => {
        const el =
          this.container.nativeElement.querySelector('button.checked') ??
          this.container.nativeElement.querySelector('button');

        if ( el instanceof HTMLElement ) {
          el.focus();
        } else {
          (document.activeElement as HTMLElement)?.blur();
        }

      }, 150);
    } else {
      this.setValue(this.value);
      (document.activeElement as HTMLElement)?.blur();
    }
  }

  private calculateMenuHeight(): void {
    setTimeout(() => {
      if ( !this.openDropdown ) {
        return;
      }

      const container = this.container?.nativeElement;

      if ( container instanceof HTMLElement ) {
        let parent = container.parentElement;
        let upperBound = 0, lowerBound = window.innerHeight;

        while ( parent ) {
          const computedStyle = getComputedStyle(parent);

          const overflows = [computedStyle.overflow, computedStyle.overflowX, computedStyle.overflowY];

          if ( overflows.some((o => o !== '' && o !== 'visible')) ) {
            const parentBox = parent.getBoundingClientRect();
            upperBound = Math.max(parentBox.top, upperBound);
            lowerBound = Math.min(parentBox.bottom, lowerBound);
          }

          parent = parent.parentElement;
        }

        const dialog = container.closest('mat-dialog-container');
        const headerBox = dialog?.querySelector('.header')?.getBoundingClientRect();
        const footerBox = dialog?.querySelector('.footer')?.getBoundingClientRect();

        if ( headerBox ) {
          upperBound = Math.max(headerBox.bottom, upperBound);
        }

        if ( footerBox ) {
          lowerBound = Math.min(footerBox.top, lowerBound);
        }


        const dropdown = container.querySelector('.dropdown');
        let dropdownFirstChildBox: DOMRect, dropdownLastChildBox: DOMRect;

        Object.values(dropdown.children).forEach(( child: HTMLElement ) => {
          if ( child.style.display !== 'none' ) {
            if ( !dropdownFirstChildBox ) {
              dropdownFirstChildBox = dropdownLastChildBox = child.getBoundingClientRect();
            } else {
              dropdownLastChildBox = child.getBoundingClientRect();
            }
          }
        });

        let height = 0;

        if ( dropdownFirstChildBox && dropdownLastChildBox ) {
          if ( dropdownFirstChildBox.top > dropdownLastChildBox.top ) {
            height = Math.min(this.maxMenuHeight, dropdownFirstChildBox.bottom - dropdownLastChildBox.top + 5);
          } else {
            height = Math.min(this.maxMenuHeight, dropdownLastChildBox.bottom - dropdownFirstChildBox.top + 5);
          }
        }

        const containerBox = container.getBoundingClientRect();

        const upperHeight = containerBox.top - upperBound - 5;
        const lowerHeight = lowerBound - containerBox.bottom - 5;

        if ( height > 10 && lowerHeight < 0.5 * height ) {
          this.upperDropdown = lowerHeight < upperHeight;
          height = Math.max(20, Math.min(Math.max(lowerHeight, upperHeight), height));
        } else if ( height > 10 ) {
          height = Math.max(20, Math.min(lowerHeight, height));
          this.upperDropdown = false;
        }

        this.calculatedMenuHeight = `${height}px`;
      }
    }, 100)

  }

  private getValue( element: any, path?: string | (( item: any ) => string) ): string {
    if ( typeof path === 'function' ) {
      return `${path(element) ?? ''}`;
    }

    const pathSplit = path?.split('.') ?? ['*'];

    for ( const prop of pathSplit ) {
      if ( prop === '*' || !element ) {
        break;
      }

      element = element[prop];
    }

    return `${element ?? ''}`;
  }

  private setValue( text: string ): void {
    this.value = text;

    if ( (this.type === 'SELECT' || this.type === 'MULTI_SELECT') && typeof this.displayedSelectedText === 'string' ) {
      this.value = this.displayedSelectedText;
    }

    if ( this.input?.nativeElement ) {
      this.input.nativeElement.value = this.value?.replace(/\n/g, ' ');
    }
  }

  private refreshValues( timeout = 200 ): void {
    if ( this.refreshTimeout !== null ) {
      return;
    }

    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.optionMap.clear();

      if ( this.type === 'SELECT' || this.type === 'MULTI_SELECT' || this.type === 'SUGGEST' ) {
        if ( this.options ) {
          for ( const option of this.options ) {
            let label = this.getValue(option, this.namePropertyPath);

            if ( this.translateOptions ) {
              label = TranslationDirective.data?.[label] ?? label;
            }

            this.optionMap.set(this.getValue(option, this.valuePropertyPath), {
              label: this.nameIsHtml ? TextHelper.getInnerText(label).trim() : label,
              html: this.nameIsHtml ? this.sanitizer.bypassSecurityTrustHtml(label) : undefined,
              image: this.imagePropertyPath ? this.getValue(option, this.imagePropertyPath) : undefined,
              style: option.style,
              id: option.id,
            });
          }
        }

        if ( this.selectedOptionsOrValues !== undefined && !(this.selectedOptionsOrValues instanceof Date) ) {
          this.selectedValues.clear();

          const valuePath = (this.selectedOptionsOrValues instanceof Object) ? (this.selectedValuePropertyPath ?? this.valuePropertyPath) : '*';
          const iterable = (this.selectedOptionsOrValues instanceof Object) ? this.selectedOptionsOrValues : (this.selectedOptionsOrValues !== null ? [`${this.selectedOptionsOrValues}`] : []);


          for ( const value of iterable ) {
            this.selectedValues.add(this.getValue(value, valuePath));

            if ( this.type === 'SELECT' ) {
              break;
            }
          }
        }

        if ( this.displayedOptions.length === this.optionMap.size ) {
          let matches = true;
          for ( const opt of this.displayedOptions ) {
            if (
              opt.label !== this.optionMap.get(opt.value)?.label ||
              opt.image !== this.optionMap.get(opt.value)?.image ||
              opt.selected !== this.selectedValues.has(opt.value)
            ) {
              matches = false;
              break;
            }
          }

          if ( matches ) {
            return;
          }
        }

        this.displayedOptions = [...this.optionMap].map(entry => ({
          label: entry[1].label,
          html: entry[1].html,
          image: entry[1].image,
          style: entry[1].style,
          id: entry[1].id,
          value: entry[0],
          selected: this.selectedValues.has(entry[0])
        }));

        this.value = [...this.selectedValues].map(x => this.optionMap.get(x)?.label).join(', ');

        if ( !(this.type === 'MULTI_SELECT' && this.openDropdown) && !(this.enableSearch && this.openDropdown) ) {
          this.setValue(this.value);
        }

        this.calculateMenuHeight();
      } else {
        this.openDropdown = false;
        this.selectedValues.clear();
        this.displayedOptions.length = 0;

        if ( typeof this.selectedOptionsOrValues === 'string' || typeof this.selectedOptionsOrValues === 'number' ) {
          this.setValue(this.selectedOptionsOrValues.toString());
        } else if ( this.selectedOptionsOrValues === null ) {
          this.setValue('');
        } else if ( this.selectedOptionsOrValues instanceof Date ) {
          this.displayDateAndTime(this.selectedOptionsOrValues);
        }
      }
    }, timeout)
  }

  displayDateAndTime( date: Date, emitChange = false ): void {
    if ( !date ) {
      this.setValue('');
    } else {
      if ( this.type === 'TIME' ) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        this.selectedHour = date.getHours();
        this.selectedMinute = date.getMinutes();

        this.setValue(`${hours}:${minutes}`);
      } else if ( this.type === 'DAY' ) {
        this.selectedYear = date.getFullYear();
        this.selectedMonth = date.getMonth();
        this.selectedDay = date.getDate();

        this.setValue(date.toLocaleDateString('ro-RO'));
      } else {
        this.setValue(date.toISOString());
      }
    }

    if ( emitChange ) {
      if ( this.type === 'DAY' ) {
        this.showDateOrTimeDropdown = false;
      }

      if ( this.type === 'DAY' || this.type === 'TIME' ) {
        this.onChange.emit(this.value);

        if ( this.notifyAboutUnsavedChanges ) {
          ActionHelper.notifyAboutEdit();
        }

        if ( !date ) {
          this.onDateTimeChange.emit(null);

          if ( this.notifyAboutUnsavedChanges ) {
            ActionHelper.notifyAboutEdit();
          }

          return;
        }

        date = new Date(date);

        let minute = date.getMinutes();
        let hour = date.getHours();
        minute -= minute % 15;
        hour = Math.min(hour, 22);
        hour = Math.max(hour, 6);
        date.setMinutes(minute);
        date.setHours(hour);
        this.onDateTimeChange.emit(date);

        if ( this.notifyAboutUnsavedChanges ) {
          ActionHelper.notifyAboutEdit();
        }
        return;
      }
    }
  }

  selectHour( number: number ): void {
    this.selectedHour = number;
    const date = this.selectedOptionsOrValues instanceof Date ? new Date(this.selectedOptionsOrValues) : new Date();
    date.setHours(number);
    this.displayDateAndTime(date, true);
  }

  selectMinute( number: number ): void {
    this.selectedMinute = number;
    const date = this.selectedOptionsOrValues instanceof Date ? new Date(this.selectedOptionsOrValues) : new Date();
    date.setMinutes(number);
    this.displayDateAndTime(date, true);
  }
}
